import { useEffect, useState, useCallback, useRef } from "react";
import { toast } from '../../common/Toast/toast';
import PopupDone, { usePopupDone } from "../../common/PopupDone/PopupDone.jsx";
import { SlidersHorizontal } from "lucide-react";
import NewCustomDataTable from "../../common/NewCustomDataTable.jsx";
import SettingsFormModal from "../../common/SettingsFormModal.jsx";
import AddOM from "./AddOM.jsx";
import useScrollLoadMore from "../../common/useScrollLoadMore";
import {
  getOperationsToModules,
  deleteOperationToModule,
} from "../../../../services/OMService.js";
// Built to the same plan as /list-attendance, so it reads that page's
// single stylesheet rather than keeping one of its own.
import "../../Trainer/TrainerDashboard.css";
import FilterSheet from "../../common/FilterSheet/FilterSheet";
import useDebouncedCallback from "../../common/useDebouncedCallback";
import { confirm } from '../../common/Confirm/confirm';

const DASH = "—";

/* A bare 24-character hex string is a database id, not a name. The old
   list printed one in the Module column whenever the join had not
   brought a label back, and again in the popup -- so the page showed
   things like 69d4b3695aff1cc96c556091 where a module name belonged. */
const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || "").trim());

const dateOf = (value) => {
  if (!value) return DASH;
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? DASH
    : d.toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
};

/* The module this record binds operations to. Only ever its name. */
const moduleNameOf = (row) => {
  const label = row?.module?.label || row?.module?.name || "";
  if (String(label).trim()) return String(label).trim();
  // Nothing joined. The id is not an answer, so there is no answer.
  return "";
};

/* The operations this record carries, split into the ones that came back
   with a name and a count of the ones that are stored as an id only.
   Reported separately rather than mixed, so the row's count stays true
   while the popup only ever shows readable values. */
const operationsOf = (row) => {
  const raw = row?.operationIds;
  let entries = [];

  if (Array.isArray(raw)) entries = raw;
  else if (typeof raw === "string") {
    entries = raw
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }

  const named = [];
  let unnamed = 0;

  entries.forEach((entry) => {
    const value =
      entry && typeof entry === "object"
        ? entry.label || entry.name || entry.value || ""
        : entry;
    const text = String(value || "").trim();
    if (!text || isObjectId(text)) unnamed += 1;
    else named.push(text);
  });

  return { total: entries.length, named, unnamed };
};

/* ── The record popup ────────────────────────────────────────────────
   Same plan as the attendance list's popup: title and a pgm-ident row of
   labelled facts in the header, pgm-section blocks in the body each led
   by a pgm-section-title, pgm-table for anything with more than one
   value, pgm-empty when there is nothing, and a footer whose actions sit
   left of a filled Close.
   ------------------------------------------------------------------ */
const OMDetailModal = ({ detail, onClose, onEdit, onDelete }) => {
  useEffect(() => {
    if (!detail) return undefined;
    const onKey = (e) => {
      // A form opened from here sits on top; Escape is its, not this popup's.
      if (e.key === "Escape" && !document.querySelector(".pg-modal--over")) onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [detail, onClose]);

  // A delete from here swaps the record for the done panel, which closes
  // the popup five seconds later.
  const { done } = usePopupDone(Boolean(detail));

  if (!detail) return null;

  const { label, value, createdAt, updatedAt, createdBy, updatedBy } = detail;
  const moduleName = moduleNameOf(detail);
  const ops = operationsOf(detail);

  const facts = [
    { label: "Label", value: label || DASH },
    { label: "Value", value: value || DASH },
    { label: "Module", value: moduleName || DASH },
    { label: "Created", value: dateOf(createdAt) },
    { label: "Last updated", value: dateOf(updatedAt) },
  ];

  /* Who touched the record, and only when the record says. The old popup
     printed "N/A" for both on every row that had never carried them,
     which reads as a finding rather than as an absence. */
  const authorship = [
    ...(createdBy ? [{ label: "Created by", value: createdBy }] : []),
    ...(updatedBy ? [{ label: "Updated by", value: updatedBy }] : []),
  ];

  return (
    <div
      className="pg-modal"
      role="dialog"
      aria-modal="true"
      aria-labelledby="om-detail-title"
      onClick={onClose}
    >
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        onClick={(e) => e.stopPropagation()}
      >
        {/* The binding is what the record is about, so its label is the
            title -- in full, never clipped the way the list clips it. */}
        <header className="pgm-head">
          <div className="pgm-head-main">
            <h2 className="pgm-batch" id="om-detail-title">
              {label || "Operation to module"}
            </h2>
            <dl className="pgm-ident">
              <div className="pgm-ident-row">
                <dt>Module</dt>
                <dd>{moduleName || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Value</dt>
                <dd>{value || DASH}</dd>
              </div>
              <div className="pgm-ident-row">
                <dt>Created</dt>
                <dd>{dateOf(createdAt)}</dd>
              </div>
            </dl>
          </div>

          <button
            type="button"
            className="pg-modal-close"
            onClick={onClose}
            aria-label="Close"
          >
            &times;
          </button>
        </header>

        <div className="pg-modal-body">
          {done ? (
            <PopupDone message={done} onClose={onClose} />
          ) : (
            <>
              {/* The operations first: what the record actually grants, before
                  the bookkeeping around it. */}
              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Operations
                  <span className="pgm-section-count">{ops.total}</span>
                </h3>

                {ops.named.length === 0 ? (
                  <p className="pgm-empty">
                    {ops.total === 0
                      ? "This record grants no operations."
                      : `${ops.total} operation${
                          ops.total === 1 ? " is" : "s are"
                        } attached, but the record stores only their ids -- no names came back to show.`}
                  </p>
                ) : (
                  <>
                    <table className="pgm-table">
                      <thead>
                        <tr>
                          <th className="pgm-num">#</th>
                          <th>Operation</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ops.named.map((op, i) => (
                          <tr key={`${op}-${i}`}>
                            <td className="pgm-num">{i + 1}</td>
                            <td>{op}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {ops.unnamed > 0 && (
                      <p className="pgm-empty">
                        {ops.unnamed} more {ops.unnamed === 1 ? "is" : "are"} stored
                        as an id only and cannot be named here.
                      </p>
                    )}
                  </>
                )}
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">Record</h3>
                <table className="pgm-table">
                  <thead>
                    <tr>
                      <th className="pgm-num">#</th>
                      <th>Detail</th>
                      <th>Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facts.map((f, i) => (
                      <tr key={f.label}>
                        <td className="pgm-num">{i + 1}</td>
                        <td>{f.label}</td>
                        <td>{f.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </section>

              <section className="pgm-section">
                <h3 className="pgm-section-title">
                  Authorship
                  <span className="pgm-section-count">{authorship.length}</span>
                </h3>

                {authorship.length === 0 ? (
                  <p className="pgm-empty">
                    This record does not say who created or last changed it.
                  </p>
                ) : (
                  <table className="pgm-table">
                    <thead>
                      <tr>
                        <th className="pgm-num">#</th>
                        <th>Detail</th>
                        <th>Value</th>
                      </tr>
                    </thead>
                    <tbody>
                      {authorship.map((f, i) => (
                        <tr key={f.label}>
                          <td className="pgm-num">{i + 1}</td>
                          <td>{f.label}</td>
                          <td>{f.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </section>
            </>
          )}
        </div>

        {/* What you can do with the record sits with Close, at the end.
            Close stays last and is the only filled button, so the
            destructive one is never the default under the cursor. */}
        {/* The done panel replaces the actions once a delete lands. */}
        {!done && (
          <footer className="pg-modal-foot">
            <div className="pgm-actions">
              <button type="button" className="pgm-btn" onClick={onEdit}>
                Edit
              </button>
              <button
                type="button"
                className="pgm-btn pgm-btn--danger"
                onClick={onDelete}
              >
                Delete
              </button>
            </div>
            <button type="button" className="pg-btn pg-btn--main" onClick={onClose}>
              Close
            </button>
          </footer>
        )}
      </div>
    </div>
  );
};

const OMList = () => {
  const [data, setData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  // 25 a batch. The list scrolls rather than paging, so this is how much
  // arrives per fetch, not how much the reader has to click through.
  const [recordsPerPage, setRecordsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  // Fetching the next scroll batch. Kept apart from `loading` because that
  // one draws a veil over the whole table -- right for a fresh load, wrong
  // for adding rows to the bottom of a list being read.
  const [loadingMore, setLoadingMore] = useState(false);
  const [detail, setDetail] = useState(null);
  /* The add/edit dialog. null is closed; {} is a new record; { recordId }
     edits that one. The form is the same component the route renders --
     it just reports back here instead of navigating. */
  const [formModal, setFormModal] = useState(null);
  /* Which step the form is on, and its question -- the dialog head asks
     it. Cleared with the dialog, so the next open starts on step one's
     copy rather than flashing the step the last one ended on. */
  const [formStep, setFormStep] = useState(null);

  // Closing the dialog and forgetting the step it was on are the same act,
  // so nothing can do one without the other.
  const closeForm = useCallback(() => {
    setFormModal(null);
    setFormStep(null);
  }, []);

  // What is typed into the filter section but not yet applied.
  const [searchDraft, setSearchDraft] = useState("");
  const [searchFieldDraft, setSearchFieldDraft] = useState("label");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedField, setSelectedField] = useState("label");
  const isFetching = useRef(false);
  const appendRef = useRef(false);
  const listRef = useRef(null);
  // The foot under the list. It is what the observer watches, and it
  // sits in the normal flow of the page -- no pane of its own.
  const listEndRef = useRef(null);

  // ─── Fetch ────────────────────────────────────────────────────────────

  const fetchData = useCallback(async () => {
    if (isFetching.current) return;
    isFetching.current = true;
    // Captured before the request: the overlay must never appear for a
    // scroll batch, and reading the live ref after an await could get the
    // wrong answer if anything reset it meanwhile.
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);

    try {
      const searchFieldValue = selectedField !== "all" ? selectedField : "";
      const res = await getOperationsToModules(
        recordsPerPage,
        currentPage,
        searchTerm,
        searchFieldValue,
      );

      const pageData = res.data || [];
      setTotalRecords(Number(res.pagination?.total || 0));

      // Guarded by id so a repeated page cannot double up a row.
      if (isAppend) {
        setData((prev) => {
          const seen = new Set(prev.map((r) => r.id || r._id));
          return [...prev, ...pageData.filter((r) => !seen.has(r.id || r._id))];
        });
      } else {
        setData(pageData);
      }
      appendRef.current = false;
    } catch (error) {
      console.error("Error fetching OM:", error);
      if (!isAppend) {
        setData([]);
        setTotalRecords(0);
      }
      appendRef.current = false;
      toast.error("Failed to fetch Operation to Module list. Please try again.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
  }, [recordsPerPage, currentPage, searchTerm, selectedField]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // ─── Scroll ───────────────────────────────────────────────────────────

  // The scroll asked for the next batch. Only ever moves forward, and only
  // while there is more on the server than is already on screen.
  const loadMore = useCallback(() => {
    if (isFetching.current || loading || loadingMore) return;
    if (data.length >= totalRecords) return;
    // Derived from the rows already on screen rather than counted up, so
    // the batch size can change mid-list without asking for a page that
    // has already been read. Any overlap a ragged division causes is
    // dropped by the id guard where the rows are appended.
    const nextPage = Math.floor(data.length / recordsPerPage) + 1;
    // Setting the page to what it already is changes no state and runs no
    // effect, which would strand appendRef at true and make the next fresh
    // load append to the list it was supposed to replace.
    if (nextPage === currentPage) return;
    appendRef.current = true;
    setCurrentPage(nextPage);
  }, [
    loading,
    loadingMore,
    data.length,
    totalRecords,
    recordsPerPage,
    currentPage,
  ]);

  // The page is the scroller, so the foot of the list coming into view is
  // the signal for the next batch -- see useScrollLoadMore.
  useScrollLoadMore(listEndRef, loadMore, [loadMore, data.length]);

  // ─── Handlers ─────────────────────────────────────────────────────────

  /* The search box does not wait for Apply. It runs itself once the
     typing stops -- the pickers beside it still wait, because
     choosing three of those should cost one refetch, not three. */
  const runSearch = useDebouncedCallback((term, field) => {
    appendRef.current = false;
    setSearchTerm(term.trim());
    setSelectedField(field);
    setCurrentPage(1);
  });

  const handleApplyFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchTerm(searchDraft.trim());
    setSelectedField(searchFieldDraft);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    /* This sets the search itself, so a keystroke still waiting out
       its delay must not land on top of it a moment later. */
    runSearch.cancel();
    appendRef.current = false;
    setSearchDraft("");
    setSearchFieldDraft("label");
    setSearchTerm("");
    setSelectedField("label");
    setCurrentPage(1);
  };

  const handleRowsPerPageChange = (newLimit) => {
    appendRef.current = false;
    setRecordsPerPage(Number(newLimit));
    setCurrentPage(1);
  };

  const hasActiveFilters = Boolean(searchTerm) || selectedField !== "label";

  const handleDelete = async (id) => {
    const result = await confirm({
      title: "Delete Confirmation",
      tone: 'danger',
      text: "Are you sure you want to delete this Operation to Module?",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
    });

    if (!result.isConfirmed) return;

    try {
      await deleteOperationToModule(id);
      // Gone: the popup that showed it swaps to the done panel and closes
      // itself five seconds later.
      toast.success("Operation to Module has been deleted.");
      // Rebuild the list from page 1 so the row is gone and the count is
      // right, rather than leaving a hole in a scrolled list.
      appendRef.current = false;
      if (currentPage === 1) fetchData();
      else setCurrentPage(1);
    } catch (error) {
      console.error("Error deleting OM:", error);
      toast.error("Failed to delete Operation to Module. Please try again.");
    }
  };

  // ─── Headers ──────────────────────────────────────────────────────────

  /* Five columns and no more. Sr No, the operation names, who wrote the
     record and the row of action buttons are all in the popup a row opens
     -- the same trade the attendance list makes.

     Operations is a count here, not the joined list it used to be: a
     record binding eight of them wrote all eight names into one cell and
     pushed every column after it off the row. */
  const headers = [
    {
      title: "Label",
      prop: "label",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.label ? (
          <span className="pg-strong">{row.label}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        ),
    },
    {
      title: "Value",
      prop: "value",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) =>
        row.value ? <span>{row.value}</span> : <span className="pg-none">{DASH}</span>,
    },
    {
      title: "Module",
      prop: "moduleId",
      searchable: true,
      filterable: true,
      sortable: true,
      cell: (row) => {
        const name = moduleNameOf(row);
        return name ? <span>{name}</span> : <span className="pg-none">{DASH}</span>;
      },
    },
    {
      title: "Operations",
      prop: "operationIds",
      cell: (row) => {
        const { total } = operationsOf(row);
        return total > 0 ? (
          <span className="pg-pill">{total}</span>
        ) : (
          <span className="pg-none">{DASH}</span>
        );
      },
    },
    {
      title: "Created",
      prop: "createdAt",
      filterable: true,
      sortable: true,
      cell: (row) => <span className="pg-date">{dateOf(row.createdAt)}</span>,
    },
  ];

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div className="pg">

      <SettingsFormModal
        open={Boolean(formModal)}
        /* The form is two steps and reports which one it is on, so the
           dialog asks the step's question rather than repeating a fixed
           title over changing content. The fallbacks are what shows for
           the frame before the first report arrives. */
        eyebrow={formModal?.recordId ? "Edit operation to module" : "New operation to module"}
        title={formStep?.title || "What is it, and which module?"}
        subtitle={
          formStep?.hint ||
          "A name, the key permission checks are written against, and the module this belongs to."
        }
        labelledBy="om-form-title"
        size="wide"
        onClose={closeForm}
      >
        <AddOM
          embedded
          recordId={formModal?.recordId}
          onStepChange={(copy) => setFormStep(copy)}
          onCancel={closeForm}
          onDone={() => {
            // Saved: the dialog swaps the form for the done panel and closes
            // itself five seconds later (SettingsFormModal).
            // The record changed, so the view popup still open behind this form
            // would be showing the old one. It goes; the form shows the done panel.
            setDetail(null);
            // Rebuild from page 1 so a new record appears and an edited one
            // shows its new values.
            appendRef.current = false;
            if (currentPage === 1) fetchData();
            else setCurrentPage(1);
          }}
        />
      </SettingsFormModal>

      <OMDetailModal
        detail={detail}
        onClose={() => setDetail(null)}
        onEdit={() => {
          const id = detail?.id || detail?._id;
          if (id) setFormModal({ recordId: String(id) });
        }}
        onDelete={() => {
          const id = detail?.id || detail?._id;
          if (id) handleDelete(id);
        }}
      />

      {/* Every control that narrows the list lives here, the search box
          included -- the table's own toolbar is switched off below so the
          two are not stacked one above the other. Nothing here takes
          effect until Apply, so changing two fields is one refetch. */}
      <FilterSheet label="Filter operations to modules">
        {/* Row one: the search box, the field it searches, and how many
            rows a load brings. */}
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="om-search">
              Search
            </label>
            <input
              id="om-search"
              type="text"
              className="pg-input pg-input--search"
              placeholder="Search by label, value or module..."
              value={searchDraft}
              onChange={(e) => {
                setSearchDraft(e.target.value);
                runSearch(e.target.value, searchFieldDraft);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleApplyFilters();
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="om-search-field">
              Search in
            </label>
            <select
              id="om-search-field"
              className="pg-select"
              value={searchFieldDraft}
              onChange={(e) => {
                setSearchFieldDraft(e.target.value);
                if (searchDraft.trim()) runSearch.flush(searchDraft, e.target.value);
              }}
            >
              <option value="all">All fields</option>
              <option value="label">Label</option>
              <option value="value">Value</option>
              <option value="moduleId">Module</option>
            </select>
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="om-rows">
              Rows per load
            </label>
            <select
              id="om-rows"
              className="pg-select"
              value={recordsPerPage}
              onChange={(e) => handleRowsPerPageChange(e.target.value)}
            >
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>
        </div>

        {/* Row two: the buttons. */}
        <div className="pg-filters-row">
          <div className="pg-filters-fields pg-filters-fields--notrainer" />

          <div className="pg-filters-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={handleApplyFilters}
            >
              Apply
            </button>
            <button
              type="button"
              className="pg-btn"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </button>

            <span className="pg-filters-sep" aria-hidden="true" />

            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({})}
            >
              Add operation to module
            </button>
          </div>
        </div>
      </FilterSheet>

      {/* --optomod: the rules that give a row its pointer and hover are
          unscoped, so the name has to be its own and unmistakable. */}
      <div className="td-list td-list--optomod" ref={listRef}>
        <NewCustomDataTable
          data={data}
          headers={headers}
          totalRecords={totalRecords}
          currentPage={currentPage}
          recordsPerPage={recordsPerPage}
          onPageChange={setCurrentPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          serverSide={true}
          loading={loading}
          loadingMore={loadingMore}
          showToolbar={false}
          showPagination={false}
          showFooter={false}
          onRowClick={(row) => setDetail(row)}
          // Every cell wraps its text in a span, so the default
          // ['TR', 'TD'] would leave the text itself unclickable and only
          // the padding around it would open the record.
          validRowClickTagNames="*"
          rowProps={(row) => ({
            tabIndex: 0,
            role: "button",
            "aria-label": `Open ${row.label || "this record"}`,
            onKeyDown: (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setDetail(row);
              }
            },
          })}
        />
      </div>

      {/* What the page buttons used to say, said once: how far down the
          list the reader is. Scrolling near the bottom fetches the next
          batch. */}
      <div className="pg-scroll-foot" ref={listEndRef}>
        {data.length < totalRecords ? (
          <>
            <span className="pg-scroll-count">
              {data.length} of {totalRecords}
            </span>
            {loadingMore && <span className="pg-spinner" aria-hidden="true" />}
          </>
        ) : (
          totalRecords > 0 && (
            <span className="pg-scroll-count">
              All {totalRecords} record{totalRecords === 1 ? "" : "s"} loaded
            </span>
          )
        )}
      </div>

      {!loading && data.length === 0 && (
        <div className="pg-state">
          <span className="pg-state-icon">
            <SlidersHorizontal size={26} aria-hidden="true" />
          </span>
          <h3 className="pg-state-title">No records found</h3>
          <p className="pg-state-text">
            {searchTerm
              ? `Nothing matches “${searchTerm}”.`
              : "No operation has been bound to a module yet."}
          </p>
        </div>
      )}
    </div>
  );
};

export default OMList;
